import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { CourseDetail as CourseDetailType, CourseProgress } from '../../types'
import { Alert, Badge, Card, CardBody, CardHeader, Skeleton, StatusBadge } from '../../components/ui'
import { CheckIcon, ChartIcon } from '../../components/icons'

export default function CourseDetail() {
  const { courseId } = useParams<{ courseId: string }>()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['learning', 'course', courseId],
    queryFn: () => api.get<{ ok: boolean; course: CourseDetailType; progress: CourseProgress | null }>(
      `/learning/courses/${courseId}`,
    ),
    enabled: Boolean(courseId),
  })

  if (!courseId) return <Navigate to="/learning" replace />

  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/learning">Back to courses</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) {
    return <div className="stack"><Skeleton h={30} w="50%" /><Skeleton h={140} /></div>
  }

  const { course, progress } = data
  const allModulesDone = course.modules.every((m) => m.completed)
  const finalExamPassed = progress?.final_exam_passed ?? false

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{course.course_name}</h1>
          <p className="page-head__sub">{course.course_description}</p>
        </div>
      </div>

      <div className="grid grid--tiles-2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardBody>
            <div className="stat__label">Duration</div>
            <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>
              {course.estimated_duration || '—'}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="stat__label">Your progress</div>
            <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>
              {progress?.progress_pct ?? 0}%
            </div>
          </CardBody>
        </Card>
      </div>

      {course.learning_outcomes && course.learning_outcomes.length > 0 && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="What you'll learn" />
          <CardBody>
            <ul style={{ margin: 0, paddingLeft: '1.2em' }} className="stack">
              {course.learning_outcomes.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Modules" sub={`${course.modules.length} in total`} />
        <CardBody flush>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th /><th>Module</th><th /><th /></tr>
              </thead>
              <tbody>
                {course.modules.map((m, i) => (
                  <tr key={m.module_id ?? i}>
                    <td style={{ width: 28 }}>
                      {m.completed ? <CheckIcon size={16} /> : <span style={{ color: 'var(--text-muted)' }}>{i + 1}</span>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{m.module_name}</div>
                      {m.module_description && (
                        <div className="page-head__sub" style={{ margin: 0 }}>{m.module_description}</div>
                      )}
                    </td>
                    <td>{m.has_assessment && <Badge tone="info">Has quiz</Badge>}</td>
                    <td className="table__num">
                      <Link to={`/learning/${courseId}/modules/${i}`}>
                        {m.completed ? 'Review' : 'Start'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card style={{ marginTop: 'var(--sp-6)' }}>
        <CardBody>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Final assessment</div>
              <div className="page-head__sub" style={{ margin: 0 }}>
                {finalExamPassed
                  ? `Passed with ${progress?.final_exam_score ?? 0}%`
                  : allModulesDone
                    ? 'All modules complete - you can take the final assessment now.'
                    : 'Complete every module first to unlock this.'}
              </div>
            </div>
            {finalExamPassed ? (
              <StatusBadge status="completed" />
            ) : (
              <Link
                to={`/learning/${courseId}/assessment`}
                aria-disabled={!allModulesDone}
                style={!allModulesDone ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
              >
                <span className="btn btn--primary btn--sm">
                  <ChartIcon size={15} /> Take final assessment
                </span>
              </Link>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  )
}
